import { booleanPointInPolygon, Position } from "@turf/turf";
import { useNotification } from "app/NotificationProvider";
import { AnalyticsEvents, mixpanel } from "common/analytics";
import { AnimalsLocationIcon } from "common/icons";
import { useTranslation } from "common/locales";
import { moveAnimalsIn } from "features/singleCoverInput/moveAnimalsIn";
import type { Geometry } from "geojson";
import { Field } from "model";
import { useState } from "react";
import { Marker } from "react-map-gl/mapbox";
import { useMatch, useNavigate } from "react-router-dom";
import { useUIStore } from "stores";

const MIN_TOUCH_TARGET = 44;

interface AnimalsLocationMarkerProps {
  htmlColor: string;
  zoom: number;
  coordinates: Position;
  currentField: Field;
  fields: Field[];
}

export default function AnimalsLocationMarker({
  htmlColor,
  zoom,
  coordinates,
  currentField,
  fields,
}: AnimalsLocationMarkerProps) {
  const [isDragging, setIsDragging] = useState<boolean>(false);
  // Use Zustand for UI actions
  const openDrawer = useUIStore((state) => state.openDrawer);
  const setIsDraggingAnimalMarker = useUIStore(
    (state) => state.setIsDraggingAnimalMarker
  );

  const { notify } = useNotification();
  const navigate = useNavigate();
  const { t } = useTranslation();
  let width = 24; // non-standard size
  let height = 31; // non-standard size
  let fontSize: "medium" | "small" = "medium";
  if (zoom > 15) {
    fontSize = "small";
    width = 20;
    height = 25.6;
  }

  const padX = Math.max(0, (MIN_TOUCH_TARGET - width) / 2);
  const padY = Math.max(0, (MIN_TOUCH_TARGET - height) / 2);

  const initialScreenSelectMatch = useMatch("/initial-screen-select/*");

  const recordsScreenMatch = useMatch("/records/*");

  return (
    <div
      onClick={() => {
        if (!isDragging && currentField.animalGroup) {
          navigate(`/animal-group/${currentField.animalGroup}`);
          openDrawer();
        }
      }}
    >
      <Marker
        offsetTop={-height / 2}
        offsetLeft={-width / 2}
        longitude={coordinates[0]}
        latitude={coordinates[1]}
        anchor="center"
        style={{ touchAction: "none", cursor: "grab" }}
        draggable={!recordsScreenMatch && !initialScreenSelectMatch}
        onDragStart={() => {
          setIsDragging(true);
          setIsDraggingAnimalMarker(true);
        }}
        onDragEnd={async (e) => {
          setTimeout(() => {
            // setTimeout to prevent onClick event of the outer div when dragging the marker
            setIsDragging(false);
            setIsDraggingAnimalMarker(false);
          }, 100);

          await onMoveIn(e.lngLat);
        }}
      >
        <div
          style={{
            padding: `${padY}px ${padX}px`,
            margin: `-${padY}px -${padX}px`,
          }}
        >
          <AnimalsLocationIcon
            htmlColor={htmlColor}
            fontSize={fontSize || "small"}
          />
        </div>
      </Marker>
    </div>
  );

  async function onMoveIn(lngLat: { lng: number; lat: number }) {
    const point: [number, number] = [lngLat.lng, lngLat.lat];
    let newField: Field | null = null;
    for (const field of fields) {
      if (booleanPointInPolygon(point, field.geom.geometry as Geometry)) {
        newField = field;
        break;
      }
    }
    if (
      !newField ||
      newField.guid === currentField.guid ||
      newField.animalGroup !== currentField.animalGroup
    ) {
      return;
    }

    if (currentField) {
      useUIStore.getState().openMoveOutEventDialog({
        open: true,
        newFieldWithAnimals: newField,
        currentFieldWithAnimals: currentField,
      });
    } else {
      await moveAnimalsIn(newField, currentField, new Date(), notify, t);
    }
    mixpanel.track(AnalyticsEvents.ANIMAL_MOVE_DRAG_COMPLETED);
  }
}
